<template>
  <div class="container-fluid">
    <div class="row starter-main mt-5 pt-5"> 
      <TableTitle
        :title="title"
        pageTitle="Action Categories"
        icon="fas fa-layer-group"
        :showAction="true"
        actionLabel="Create New"
        modal="createNew"
        :disabled="!canCreate('agents')"
      />
      <CommonTable2
        :tableObj="tableConfig" 
        @edit="handleEdit"
        @delete="handleDelete"
        @view="handleView"
        permissionKey="agents"
      />
    </div>
  </div>

  <div class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4 px-3">
    <div class="d-flex align-items-center gap-2">
      <span class="text-muted">Rows per page:</span>
      <select
        class="form-select form-select-sm rows-select"
        v-model="tablePagination.per_page"
        @change="changeRows"
      >
        <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
          {{ n }}
        </option>
      </select>
    </div>
    <div class="pagination-mini">
      <button
        class="page-btn prev_btn"
        :disabled="!tablePagination.has_prev"
        @click="handlePageChange(tablePagination.current_page - 1)"
      >
        «
      </button>
      <button
        v-for="(p, index) in visiblePages"
        :key="index"
        class="page-btn"
        :class="{
          'page-active': p === tablePagination.current_page,
          'disabled-dots': p === '...',
        }"
        :disabled="p === '...'"
        @click="p !== '...' && typeof p === 'number' && handlePageChange(p)"
      >
        {{ p }}
      </button>
      <button
        class="page-btn next_btn"
        :disabled="!tablePagination.has_next"
        @click="handlePageChange(tablePagination.current_page + 1)"
      >
        »
      </button>
    </div>
  </div>

  <BaseModal
    id="createNew"
    :title="`Create ${pageTitle}`"
    :visible="isCreateModalOpen"
    @close="closeCreateModal"
    @save="handleSaveCreate"
    :save-button-disabled="isCreateFormValid"
    saveButtonText="Create"
  >
    <template #body>
      <BaseForm
        :fields="createFormFields"
        v-model="newItemData"
        @submit="handleSaveCreate"
        ref="createFormRef"
      />
    </template>
  </BaseModal>

  <BaseModal
    id="editModal"
    :title="`${isViewMode ? 'View' : 'Edit'} ${pageTitle}`"
    :visible="isEditModalOpen"
    @close="closeEditModal"
    @save="handleSaveEdit"
    :show-save-button="!isViewMode"
    :save-button-disabled="isEditFormValid"
  >
    <template #body>
      <BaseForm
        :fields="editFormFields"
        v-model="editingItemData"
        @submit="handleSaveEdit"
        ref="editFormRef"
      />
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import { useCrud } from "@/composables/commen/useCrud";
import BaseModal from "@/components/commen/modal/BaseModal.vue";
import BaseForm, { FormField } from "@/components/commen/modal/BaseForm.vue";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue"; 
import { usePermissions } from "@/composables/usePermissions";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import apiService from "@/services/apiService";
import { toast, type Content } from "vue3-toastify";

const { canCreate } = usePermissions();
const API_RESOURCE = "cms-agent-meta/action_categories";
const ACTIONS_API = "cms-agent-meta/actions";
const pageTitle = "Action Category";
const title = "Agent Action Categories";

const crud = useCrud(API_RESOURCE);

const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const currentPage = ref(1);

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;
  if (!total || total <= 4) return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  const pages: (number | string)[] = [1];
  if (current > 4) pages.push("...");
  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);
  if (current < total - 3) pages.push("...");
  pages.push(total);
  return pages;
});

// Actions options for multiselect
const actionsOptions = ref<Array<{ _id: string; title: string }>>([]);

const fetchActions = async () => {
  try {
    const result = await apiService.get(ACTIONS_API, { page: 1, limit: 200 });
    if (Array.isArray(result.data)) {
      actionsOptions.value = result.data.map((a: any) => ({ _id: a.slug || a._id, title: a.title }));
    } else if (Array.isArray(result)) {
      actionsOptions.value = result.map((a: any) => ({ _id: a.slug || a._id, title: a.title }));
    }
  } catch (err) {
    console.error("Failed to fetch actions:", err);
  }
};

const rows = ref<any[]>([]);
const tableConfig = ref({
 columns: [
    { name: "Title", field: "title", sortable: true, width: "250px" },
    { name: "Actions", field: "actions_display", sortable: false, width: "400px" },
  ],
  data: rows,
  actions: [true],
  loading: crud.isLoading,
  pagination: tablePagination,
  showCategoryFilter: false,
  showStatusFilter: false,
  table_width: "max-content",  
});

watch(crud.data, (newVal) => {
  rows.value = (newVal || []).map((item: any) => ({
    ...item,
    actions_display: Array.isArray(item.actions) ? item.actions.join(", ") : "",
  }));
});
watch(crud.isLoading, (v) => tableConfig.value.loading = v);
watch(() => crud.pagination.value, (newVal) => {
  if (!newVal) return;
  tablePagination.value.current_page = newVal.current_page || 1;
  tablePagination.value.total_pages = newVal.total_pages || 1;
  tablePagination.value.total = newVal.total || 0;
  tablePagination.value.has_prev = tablePagination.value.current_page > 1;
  tablePagination.value.has_next = tablePagination.value.current_page < tablePagination.value.total_pages;
}, { immediate: true, deep: true });

const changeRows = () => {
  currentPage.value = 1;
  crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
};
const handlePageChange = (page: number) => {
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (tablePagination.value.total_pages && page > tablePagination.value.total_pages) return;
  currentPage.value = page;
  crud.fetchData({ page, limit: tablePagination.value.per_page });
};

const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);
const newItemData = ref({ title: "", actions: [] as string[] });
const editingItemData = ref<any>({ _id: "", title: "", actions: [] as string[] });

const createFormFields = computed<FormField[]>(() => [
  { key: "title", label: "Title", type: "text", required: true, placeholder: "Enter category title" },
  { key: "actions", label: "Actions", type: "multiselect", required: true, placeholder: "Select actions", options: actionsOptions.value },
]);
const editFormFields = computed<FormField[]>(() => [
  { key: "title", label: "Title", type: "text", required: true, placeholder: "Enter category title", readonly: isViewMode.value },
  { key: "actions", label: "Actions", type: "multiselect", required: true, placeholder: "Select actions", options: actionsOptions.value, readonly: isViewMode.value },
]);

const isCreateFormValid = computed(() => !newItemData.value.title.trim() || !newItemData.value.actions.length);
const isEditFormValid = computed(() => !editingItemData.value.title?.trim() || !editingItemData.value.actions?.length);

const handleEdit = (row: any) => {
  isViewMode.value = false;
  editingItemData.value = { ...row, actions: Array.isArray(row.actions) ? [...row.actions] : [] };
  isEditModalOpen.value = true;
};
const handleView = (row: any) => {
  isViewMode.value = true;
  editingItemData.value = { ...row, actions: Array.isArray(row.actions) ? [...row.actions] : [] };
  isEditModalOpen.value = true;
};
const handleDelete = async (id: string) => {
  if (confirm("Are you sure you want to delete this action category?")) {
    await crud.deleteItem(id);
    await crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
  }
};
const handleSaveCreate = async () => {
  if (isCreateFormValid.value) return;
  const payload = { title: newItemData.value.title, actions: newItemData.value.actions };
  const res = await crud.createItem(payload);
  if (res) {
    isCreateModalOpen.value = false;
    newItemData.value = { title: "", actions: [] };
    await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
  }
};
const handleSaveEdit = async () => {
  const id = editingItemData.value._id;
  if (!id) return;
  const payload = { title: editingItemData.value.title, actions: editingItemData.value.actions };
  try {
    crud.isLoading.value = true;
    const res = await apiService.put(API_RESOURCE, id, payload);
    if (res) {
      toast.success("Action category updated successfully" as Content, { theme: "colored" });
      isEditModalOpen.value = false;
      await crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
    }
  } catch (err: any) {
    toast.error((err.response?.data?.message || err.message || "Failed to update action category.") as Content, { theme: "colored" });
  } finally {
    crud.isLoading.value = false;
  }
};
const closeCreateModal = () => { isCreateModalOpen.value = false; newItemData.value = { title: "", actions: [] }; };
const closeEditModal = () => { isEditModalOpen.value = false; isViewMode.value = false; editingItemData.value = { _id: "", title: "", actions: [] }; };

onMounted(async () => {
  await fetchActions();
  crud.fetchData({ page: tablePagination.value.current_page, limit: tablePagination.value.per_page });
});
</script>

<style scoped>
.rows-select { width: 82px; padding: 8px 17px; font-size: 0.85rem; background-color: transparent; }
.pagination-mini { display: flex; align-items: center; border-radius: 6px; overflow: hidden; }
.page-btn { border: none; background: transparent; font-size: 1rem; padding: 7px 12px; cursor: pointer; color: #6c757d; transition: 0.2s; border: 1px solid #dee2e6; outline: none; }
.page-btn:first-child { border-top-left-radius: 6px; border-bottom-left-radius: 6px; }
.page-btn:last-child { border-top-right-radius: 6px; border-bottom-right-radius: 6px; }
.page-btn:hover { background: #e9ecef; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-active { background: #21c1d6; color: #fff; padding: 7px 12px; font-weight: 600; min-width: 28px; text-align: center; display: inline-block; border: 1px solid #21c1d6; }
.prev_btn, .next_btn { color: #21c1d6; }
</style>
